import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Listing, User, ListingType, DynamicPage, PricingConfig, ServiceItem, AnalyticsEvent, DiscountCode, ChatThread, ChatMessage } from '../types';
import BusinessCard from '../components/BusinessCard';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import { CheckCircle, XCircle, LayoutDashboard, List, Users, MessageCircle, Clock, Check, X, ArrowDownLeft, ArrowUpRight, TrendingUp, DollarSign, PieChart as PieIcon, Activity, Search, Filter, ChevronRight, ArrowLeft, Briefcase, ShoppingCart, UserPlus, Eye, Phone, Layers, Plus, Trash2, Edit, ExternalLink, Settings, Save, Star, FileText, BarChart, Video, Megaphone, Zap, LogOut, Upload, ArrowUp, ArrowDown, Award, Coins, Heart, Tag, PlusCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageEditor from '../components/Admin/PageEditor';

const DEFAULT_USER_AVATAR = 'https://tbiz.ir/profile.png';

const LISTING_TYPE_OPTIONS: { value: ListingType; label: string }[] = [
  { value: 'sale', label: 'فروش کسب‌وکار' },
  { value: 'demand', label: 'متقاضی خرید' },
  { value: 'investment', label: 'جذب سرمایه' },
  { value: 'investor', label: 'سرمایه‌گذارم' },
  { value: 'intangible', label: 'آگهی نامشهود' },
  { value: 'micro_investment', label: 'سرمایه‌گذاری خرد' },
  { value: 'philanthropic', label: 'عام‌المنفعه' },
];

const BROKERAGE_SCOPE_OPTIONS = [
  { value: 'all', label: 'همه (مستقیم و کارگزاری)' },
  { value: 'direct', label: 'فقط مستقیم' },
  { value: 'brokerage', label: 'فقط کارگزاری' },
];

// Helper to render dynamic icons
const IconMap: any = {
  Star: Star,
  MessageCircle: MessageCircle,
  FileText: FileText,
  BarChart: BarChart,
  Video: Video,
  Megaphone: Megaphone,
  Zap: Zap
};

const DashboardPage: React.FC = () => {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  
  // --- User Dashboard State ---
  const [activeTab, setActiveTab] = useState<'overview' | 'listings' | 'chats' | 'investments' | 'creator_projects' | 'community_participation' | 'settings' | 'payments'>('overview');
  const [userListings, setUserListings] = useState<Listing[]>([]);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [userTransactions, setUserTransactions] = useState<any[]>([]);
  const [userReports, setUserReports] = useState<any[]>([]);
  const [userPayments, setUserPayments] = useState<any[]>([]);
  const [adminPayments, setAdminPayments] = useState<any[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [userFormData, setUserFormData] = useState({ name: '', email: '', phone: '', avatar: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [reportForm, setReportForm] = useState({ listingId: '', title: '', content: '', type: 'performance' as 'performance' | 'financial' | 'social_impact' });
  const [isPostingReport, setIsPostingReport] = useState(false);

  // --- Admin Dashboard State ---
  const [adminTab, setAdminTab] = useState<'overview' | 'listings' | 'users' | 'chats' | 'pages' | 'settings' | 'analytics' | 'discounts' | 'payments' | 'valuation_requests'>('overview');
  const [adminListings, setAdminListings] = useState<Listing[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [adminAnalytics, setAdminAnalytics] = useState<AnalyticsEvent[]>([]);
  const [adminDiscounts, setAdminDiscounts] = useState<DiscountCode[]>([]);
  const [adminValuationRequests, setAdminValuationRequests] = useState<any[]>([]);
  const [valuationFilter, setValuationFilter] = useState<string>('all');
  const [newDiscountCode, setNewDiscountCode] = useState<Partial<DiscountCode>>({
    code: '', discountPercent: 0, maxDiscountAmount: 0, isActive: true, brokerageScope: 'all', applicableTypes: []
  });
  const [adminBrokerageFilter, setAdminBrokerageFilter] = useState<'all' | 'brokerage' | 'direct'>('all');
  const [adminCategoryFilter, setAdminCategoryFilter] = useState<string>('all');
  const [adminPages, setAdminPages] = useState<DynamicPage[]>([]);
  const [systemPages] = useState([
    { id: 'home', title: 'صفحه اصلی', path: '/' },
    { id: 'about', title: 'درباره ما', path: '/about' },
    { id: 'services', title: 'خدمات', path: '/services' },
    { id: 'valuation', title: 'ارزش‌گذاری', path: '/valuation' },
    { id: 'articles', title: 'مقالات', path: '/articles' },
  ]);
  const [adminFilter, setAdminFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [adminStats, setAdminStats] = useState<any>(null);
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [editingPageData, setEditingPageData] = useState<DynamicPage | undefined>(undefined);
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [brokerageTermsText, setBrokerageTermsText] = useState<string>('');
  const [savingBrokerageTerms, setSavingBrokerageTerms] = useState(false);
  const [selectedAnalyticsUser, setSelectedAnalyticsUser] = useState<{id: string, name: string} | null>(null);
  const [newService, setNewService] = useState<Partial<ServiceItem>>({
    title: '', description: '', price: 0, icon: 'Star', brokerageScope: 'all', applicableTypes: []
  });

  // Shared Data
  const [allSystemUsers, setAllSystemUsers] = useState<User[]>([]);

  const location = useLocation();

  const getUserAvatar = (avatar?: string | null) => avatar || DEFAULT_USER_AVATAR;

  useEffect(() => {
    if (location.state && (location.state as any).newListingCreated) {
      setShowSuccessMessage(true);
      const timer = setTimeout(() => setShowSuccessMessage(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  useEffect(() => {
    const state = location.state as any;
    if (!state) return;
    if (state.activeTab === 'chats') {
      setActiveTab('chats');
    }
  }, [location.state]);

  // Reset admin sub-selections when tab changes
  useEffect(() => {
    setIsEditingPage(false);
    setEditingPageData(undefined);
  }, [adminTab]);

  useEffect(() => {
    if (user) {
        setUserFormData({
            name: user.name,
            email: user.email || '',
            phone: user.phone,
            avatar: user.avatar || '',
        });
        loadData();
    }
  }, [user, activeTab, adminTab, isEditingPage]);

  const loadData = async () => {
      try {
          if (user?.role === 'admin') {
            // Only admin can access users endpoint
            const allUsers = await api.getAllUsers();
            setAllSystemUsers(allUsers);
            const listings = await api.getAdminListings();
            setAdminListings(listings);
            setAdminUsers(allUsers);
            const threads = await api.getChatThreads();
            setChatThreads(threads);
            if (!selectedThreadId && threads.length > 0) {
                setSelectedThreadId(threads[0].id);
            }
            const approvedListings = listings.filter(l => l.status === 'approved');
            setAdminStats({
                totalUsers: allUsers.length,
                totalListings: approvedListings.length,
                activeOffers: threads.length,
                totalMarketValue: approvedListings.reduce((sum, l) => sum + (Number(l.price) || 0), 0)
            });
            const pages = await api.getAllPages();
            setAdminPages(pages);
            const pricing = await api.getPricing();
            setPricingConfig(pricing);
            
            // Load brokerage terms
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=brokerage-terms`);
                const data = await response.json();
                if (data.success && data.terms) {
                    setBrokerageTermsText(data.terms);
                }
            } catch (error) {
                console.error("Error loading brokerage terms:", error);
            }
            
            const analytics = await api.getAnalytics();
            setAdminAnalytics(analytics);
            const discounts = await api.getDiscountCodes();
            setAdminDiscounts(discounts);
            
            // Load valuation requests
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=valuation-requests&status=all`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('tbiz_auth_token')}`,
                    },
                });
                const data = await response.json();
                if (data.success) {
                    setAdminValuationRequests(data.requests || []);
                }
            } catch (error) {
                console.error('Error loading valuation requests:', error);
            }
            
            // Load admin payments
            if (typeof api.getUserTransactions === 'function') {
                try {
                    const allTxs = await api.getUserTransactions(''); // Empty string for all
                    setAdminPayments(Array.isArray(allTxs) ? allTxs : []);
                } catch (err) {
                    console.error("Error loading admin payments:", err);
                    setAdminPayments([]);
                }
            }
          } else if (user) {
            // Non-admin cannot call /users; keep minimal local user info.
            setAllSystemUsers([user]);
            const myListings = await api.getMyListings(user.id);
            setUserListings(myListings);
            const threads = await api.getChatThreads();
            setChatThreads(threads);
            
            // Safe calls for potentially missing methods
            if (typeof api.getUserTransactions === 'function') {
                try {
                    const transactions = await api.getUserTransactions(user.id);
                    // Only include REAL investment/donation transactions, filter out listing fees
                    const investmentTxs = Array.isArray(transactions) ? transactions.filter(tx => tx.type === 'investment' || tx.type === 'donation') : [];
                    setUserTransactions(investmentTxs);
                    
                    // Set all user payments for invoice section
                    setUserPayments(Array.isArray(transactions) ? transactions : []);
                } catch (txError) {
                    console.error("Error loading user transactions:", txError);
                    setUserTransactions([]);
                    setUserPayments([]);
                }
            } else {
                console.warn('api.getUserTransactions is not defined');
            }

            if (typeof api.getUserInvestmentReports === 'function') {
                const reports = await api.getUserInvestmentReports(user.id);
                setUserReports(reports);
            } else {
                console.warn('api.getUserInvestmentReports is not defined');
            }

            const state = location.state as any;
            if (state?.activeTab === 'chats' && state?.listingId) {
                try {
                    const thread = await api.startChat(state.listingId);
                    setSelectedThreadId(thread.id);
                } catch (err) {
                    console.error('Start chat failed:', err);
                }
            } else if (!selectedThreadId && threads.length > 0) {
                setSelectedThreadId(threads[0].id);
            }
          }
      } catch (error) {
          console.error("Error loading dashboard data:", error);
      }
    };

  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedThreadId) {
        setChatMessages([]);
        return;
      }
      setChatLoading(true);
      try {
        const messages = await api.getChatMessages(selectedThreadId);
        setChatMessages(messages);
      } catch (error) {
        console.error('Error loading chat messages:', error);
        setChatMessages([]);
      } finally {
        setChatLoading(false);
      }
    };

    loadMessages();
  }, [selectedThreadId]);

  const handleSendChatMessage = async () => {
    if (!selectedThreadId) return;
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    setChatSending(true);
    try {
      await api.sendChatMessage(selectedThreadId, trimmed);
      setChatInput('');
      const [messages, threads] = await Promise.all([
        api.getChatMessages(selectedThreadId),
        api.getChatThreads()
      ]);
      setChatMessages(messages);
      setChatThreads(threads);
    } catch (error) {
      console.error('Send chat message failed:', error);
      alert('ارسال پیام انجام نشد. دوباره تلاش کنید.');
    } finally {
      setChatSending(false);
    }
  };

  // --- User Actions ---

  const handleUserDeleteListing = async (id: string) => {
    if (!window.confirm('آیا از حذف این آگهی مطمئن هستید؟')) return;
    try {
      await api.deleteListing(id);
      await loadData();
    } catch (error) {
      console.error('Delete listing failed:', error);
      alert('حذف آگهی انجام نشد. دوباره تلاش کنید.');
    }
  };

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    await updateProfile({
      name: userFormData.name,
      email: userFormData.email,
      avatar: userFormData.avatar,
    });
    setProfileLoading(false);
    alert('اطلاعات با موفقیت ذخیره شد.');
  };

  const handlePostReport = async () => {
    if (!reportForm.listingId || !reportForm.title || !reportForm.content) return;
    setIsPostingReport(true);
    try {
      const listing = userListings.find(l => l.id === reportForm.listingId);
      if (typeof api.createReport === 'function') {
        await api.createReport({
          userId: user?.id, // Ensure user ID is passed if needed
          listingId: reportForm.listingId,
          listingTitle: listing?.title || 'نامشخص',
          title: reportForm.title,
          content: reportForm.content,
          type: reportForm.type
        });
        setReportForm({ listingId: '', title: '', content: '', type: 'performance' });
        alert('گزارش با موفقیت ثبت شد.');
        loadData();
      } else {
        console.error("api.createReport is not a function");
        alert("خطا در سیستم: متد ثبت گزارش یافت نشد.");
      }
    } catch (error) {
      console.error("Failed to post report", error);
    } finally {
      setIsPostingReport(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
       setProfileLoading(true);
       try {
           const files = [e.target.files[0]];
           const urls = await api.uploadFiles(files);
           if (urls.length > 0) {
               setUserFormData({...userFormData, avatar: urls[0]});
           }
       } catch (error) {
           console.error("Upload failed", error);
           alert("خطا در آپلود آواتار");
       } finally {
           setProfileLoading(false);
       }
    }
  };

  const refreshAdminListings = async () => {
    setAdminListings(await api.getAdminListings());
  };

  // --- Admin Actions ---
  const handleApproveClick = async (id: string) => {
    await handleApprove(id);
  };

  const handleApprove = async (id: string) => {
    // Invoice number/date are generated automatically by backend after admin approval.
    await api.updateListingStatus(id, 'approved');
    await refreshAdminListings();
  };

  const handleDeleteListing = async (id: string) => {
    if (window.confirm('آیا از حذف کامل این آگهی اطمینان دارید؟ این عمل غیرقابل بازگشت است.')) {
        await api.deleteListing(id);
        await refreshAdminListings();
    }
  };

  const handleReject = async (id: string) => {
    await api.updateListingStatus(id, 'rejected');
    await refreshAdminListings();
  };

  const handleEditPage = (page: DynamicPage) => {
      setEditingPageData(page);
      setIsEditingPage(true);
  };

  const handleCreatePage = () => {
      setEditingPageData(undefined);
      setIsEditingPage(true);
  };

  const handlePageSave = async () => {
      setIsEditingPage(false);
      setEditingPageData(undefined);
      setAdminPages(await api.getAllPages()); // Refresh list
  };

  const handleDeletePage = async (id: string) => {
    if(window.confirm('آیا از حذف این صفحه اطمینان دارید؟')) {
       await api.deletePage(id);
       setAdminPages(await api.getAllPages());
    }
  };

  const getBrokerageLabel = (listing: Listing) => {
    return listing.brokerageType === 'brokerage' ? 'کارگزاری' : 'مستقیم';
  };

  const filteredAdminListings = adminListings.filter(l => {
    const normalizedStatus = normalizeDashboardListingStatus(l.status);
    // Include both 'pending' and 'payment_pending' when filter is 'pending'
    const matchesStatus = adminFilter === 'all' || 
                         normalizedStatus === adminFilter || 
                         (adminFilter === 'pending' && normalizedStatus === 'payment_pending');
    const matchesBrokerage = adminBrokerageFilter === 'all' || l.brokerageType === adminBrokerageFilter;
    const matchesCategory = adminCategoryFilter === 'all' || l.category === adminCategoryFilter;
    return matchesStatus && matchesBrokerage && matchesCategory;
  });

  const allCategories = Array.from(new Set(adminListings.map(l => l.category)));

  const getUserListingCount = (userId: string) => {
    return adminListings.filter(l => l.ownerId === userId).length;
  };

  const getUserTotalValue = (userId: string) => {
    return adminListings.filter(l => l.ownerId === userId).reduce((sum, l) => sum + (l.price || 0), 0);
  };
  
  const handleAddService = () => {
      if (!pricingConfig || !newService.title || newService.price === undefined) return;
      const serviceToAdd: ServiceItem = {
          id: Math.random().toString(36).substr(2, 9),
          title: newService.title,
          description: newService.description || '',
          price: Number(newService.price),
          icon: newService.icon as any || 'Star',
          brokerageScope: (newService.brokerageScope as any) || 'all',
          applicableTypes: Array.isArray(newService.applicableTypes) ? newService.applicableTypes as ListingType[] : [],
      };
      setPricingConfig({
          ...pricingConfig,
          services: [...pricingConfig.services, serviceToAdd]
      });
      setNewService({ title: '', description: '', price: 0, icon: 'Star', brokerageScope: 'all', applicableTypes: [] });
  };

  const handleRemoveService = (id: string) => {
      if (!pricingConfig) return;
      if (window.confirm('آیا از حذف این خدمت اطمینان دارید؟')) {
          setPricingConfig({
              ...pricingConfig,
              services: pricingConfig.services.filter(s => s.id !== id)
          });
      }
  };
  
  const handleMoveService = (index: number, direction: 'up' | 'down') => {
      if (!pricingConfig) return;
      const newServices = [...pricingConfig.services];
      if (direction === 'up' && index > 0) {
          [newServices[index], newServices[index - 1]] = [newServices[index - 1], newServices[index]];
      } else if (direction === 'down' && index < newServices.length - 1) {
          [newServices[index], newServices[index + 1]] = [newServices[index + 1], newServices[index]];
      }
      setPricingConfig({ ...pricingConfig, services: newServices });
  };

  const handleUpdateService = (id: string, field: keyof ServiceItem, value: any) => {
      if (!pricingConfig) return;
      setPricingConfig({
          ...pricingConfig,
          services: pricingConfig.services.map(s => s.id === id ? { ...s, [field]: value } : s)
      });
  };

  const handleCreateDiscount = async () => {
      if (!newDiscountCode.code || !newDiscountCode.discountPercent) return;
      await api.createDiscountCode({
          code: newDiscountCode.code,
          discountPercent: newDiscountCode.discountPercent,
          maxDiscountAmount: newDiscountCode.maxDiscountAmount || undefined,
          isActive: newDiscountCode.isActive ?? true,
          brokerageScope: newDiscountCode.brokerageScope || 'all',
          applicableTypes: Array.isArray(newDiscountCode.applicableTypes) ? newDiscountCode.applicableTypes : []
      });
      setNewDiscountCode({ code: '', discountPercent: 0, maxDiscountAmount: 0, isActive: true, brokerageScope: 'all', applicableTypes: [] });
      loadData();
  };

  const handleToggleDiscount = async (id: string, isActive: boolean) => {
      await api.toggleDiscountCode(id, isActive);
      loadData();
  };

  const handleUpdateDiscountScope = async (id: string, patch: Partial<DiscountCode>) => {
      await api.updateDiscountCodeScope(id, patch);
      loadData();
  };

  const handleDeleteDiscount = async (id: string) => {
      if (window.confirm('آیا از حذف این کد تخفیف اطمینان دارید؟')) {
          await api.deleteDiscountCode(id);
          loadData();
      }
  };

  const handleUpdateValuationStatus = async (id: string, status: string) => {
      try {
          const token = localStorage.getItem('tbiz_auth_token');
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=valuation-requests`, {
              method: 'PUT',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ id, status }),
          });
          const data = await response.json();
          if (data.success) {
              loadData();
          }
      } catch (error) {
          console.error('Error updating valuation request:', error);
      }
  };

  const handleDeleteTransaction = async (id: string) => {
      if (window.confirm('آیا از حذف این فاکتور اطمینان دارید؟ این عملیات قابل بازگشت نیست.')) {
          await api.deleteTransaction(id);
          if (selectedInvoice?.id === id) {
              setShowInvoiceModal(false);
              setSelectedInvoice(null);
          }
          loadData();
      }
  };

  // --- Helpers ---

  const formatCurrency = (val: number) => {
      if (val >= 1000000000) {
          return `${(val / 1000000000).toFixed(1).replace(/\.0$/, '')} میلیارد تومان`;
      }
      if (val >= 1000000) {
          return `${(val / 1000000).toFixed(0)} میلیون تومان`;
      }
      return val.toLocaleString() + ' تومان';
  };

  const getUserInfo = (userId: string) => {
      const u = allSystemUsers.find(user => user.id === userId);
      return u ? { name: u.name, phone: u.phone } : { name: 'کاربر ناشناس', phone: '---' };
  };

  const getListingVerificationDocs = (listing: Listing) => {
      return [
          { label: 'فایل ارزش‌گذاری', url: listing.valuationFileUrl || '' },
          { label: 'فایل گردش مالی', url: listing.financialTurnoverFileUrl || '' },
          { label: 'تاییدیه حقوقی', url: listing.legalVerificationFileUrl || '' },
          { label: 'تاییدیه فنی', url: listing.technicalVerificationFileUrl || '' },
      ];
  };

  const hasListingVerificationDocs = (listing: Listing) => {
      return getListingVerificationDocs(listing).some(d => Boolean(d.url));
  };

  const hasAllUploadedVerificationDocs = (listing: Listing) => {
      const docs = getListingVerificationDocs(listing);
      return docs.every(d => Boolean(d.url));
  };

  const hasAllApprovedVerificationDocs = (listing: Listing) => {
      return Boolean(
          listing.verificationDocsApproved &&
          listing.hasValuationFile &&
          listing.hasFinancialTurnoverFile &&
          listing.hasVerification3 &&
          listing.hasVerification4
      );
  };

  const handleAdminViewDocs = (listing: Listing) => {
      const docs = getListingVerificationDocs(listing).filter(d => d.url);
      if (docs.length === 0) {
          alert('برای این آگهی فایل مدرک بارگذاری نشده است.');
          return;
      }

      docs.forEach((doc) => {
          window.open(doc.url, '_blank', 'noopener,noreferrer');
      });
  };

  const handleApproveVerificationDocs = async (listing: Listing) => {
      if (!hasAllUploadedVerificationDocs(listing)) {
          alert('برای تایید مدارک، هر ۴ مدرک باید توسط کاربر بارگذاری شده باشد.');
          return;
      }

      await api.updateListing(listing.id, {
          hasValuationFile: true,
          hasFinancialTurnoverFile: true,
          hasVerification3: true,
          hasVerification4: true,
          verificationDocsApproved: true,
          verificationDocsApprovedAt: new Date().toISOString()
      } as any);
      await refreshAdminListings();
  };

  const toggleListingTypeSelection = (current: ListingType[] = [], type: ListingType): ListingType[] => {
      return current.includes(type) ? current.filter(t => t !== type) : [...current, type];
  };

  const handleSavePricing = async () => {
    if (pricingConfig) {
      setSavingPricing(true);
      await api.updatePricing(pricingConfig);
      setSavingPricing(false);
      alert('تنظیمات قیمت با موفقیت ذخیره شد.');
    }
  };

  const handleSaveBrokerageTerms = async () => {
    setSavingBrokerageTerms(true);
    try {
      const token = localStorage.getItem('tbiz_auth_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=brokerage-terms`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ terms: brokerageTermsText })
      });
      const data = await response.json();
      if (data.success) {
        alert('شرایط کارگزاری با موفقیت ذخیره شد.');
      } else {
        alert('خطا در ذخیره شرایط کارگزاری: ' + (data.error || 'خطای ناشناخته'));
      }
    } catch (error) {
      console.error("Error saving brokerage terms:", error);
      alert('خطا در ذخیره شرایط کارگزاری');
    } finally {
      setSavingBrokerageTerms(false);
    }
  };

  function normalizeDashboardListingStatus(status?: string): 'pending' | 'payment_pending' | 'approved' | 'rejected' {
    const raw = String(status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (!raw) return 'pending';
    if (['approved', 'publish', 'published', 'active', 'live'].includes(raw)) return 'approved';
    if (['rejected', 'reject', 'declined', 'failed_review'].includes(raw)) return 'rejected';
    if (['payment_pending', 'pending_payment', 'awaiting_payment', 'unpaid', 'payment_required'].includes(raw)) return 'payment_pending';
    if (['pending', 'pending_approval', 'awaiting_approval', 'under_review', 'submitted', 'draft'].includes(raw)) return 'pending';
    return 'pending';
  }

  function isAwaitingAdminDecision(status?: string) {
    const normalizedStatus = normalizeDashboardListingStatus(status);
    return normalizedStatus === 'pending' || normalizedStatus === 'payment_pending';
  }

  const getListingStatusBadge = (status: string) => {
      switch (normalizeDashboardListingStatus(status)) {
        case 'pending': return <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-lg flex items-center gap-1 font-bold"><Clock size={14}/> در انتظار تایید</span>;
        case 'payment_pending': return <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-lg flex items-center gap-1 font-bold"><Clock size={14}/> در انتظار پرداخت</span>;
        case 'approved': return <span className="bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded-lg flex items-center gap-1 font-bold"><CheckCircle size={14}/> منتشر شده</span>;
        case 'rejected': return <span className="bg-rose-100 text-rose-700 text-xs px-2 py-1 rounded-lg flex items-center gap-1 font-bold"><XCircle size={14}/> رد شده</span>;
        default: return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-lg flex items-center gap-1 font-bold">نامشخص</span>;
      }
  };

  // --- ADMIN DASHBOARD RENDER ---
  if (user?.role === 'admin') {
    // --- Analytics Metrics Computation ---
    const totalViews = adminAnalytics.filter(e => e.type === 'listing_view').length;
    const totalOffers = chatThreads.length;
    const conversionRate = totalViews > 0 ? ((totalOffers / totalViews) * 100).toFixed(1) : '0';

    // Top Searches
    const searchEvents = adminAnalytics.filter(e => e.type === 'search' && e.metadata?.query);
    const searchCounts = searchEvents.reduce((acc, curr) => {
        const q = curr.metadata.query.toLowerCase();
        acc[q] = (acc[q] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);
    const topSearches = (Object.entries(searchCounts) as [string, number][]).sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Top Users
    const userActivity = adminAnalytics.reduce((acc, curr) => {
        if (!curr.userId) return acc;
        if (!acc[curr.userId]) acc[curr.userId] = { name: curr.userName || 'ناشناس', count: 0 };
        acc[curr.userId].count += 1;
        return acc;
    }, {} as Record<string, {name: string, count: number}>);
    const topUsers = (Object.entries(userActivity) as [string, {name: string, count: number}][]).sort((a, b) => b[1].count - a[1].count).slice(0, 5);

    // Most Popular Listings
    const listingActivity = adminAnalytics.filter(e => e.type === 'listing_view' || e.type === 'listing_click').reduce((acc, curr) => {
        if (!curr.targetId) return acc;
        if (!acc[curr.targetId]) acc[curr.targetId] = { title: curr.targetTitle || 'آگهی نامشخص', count: 0 };
        acc[curr.targetId].count += 1;
        return acc;
    }, {} as Record<string, {title: string, count: number}>);
    const topListings = (Object.entries(listingActivity) as [string, {title: string, count: number}][]).sort((a, b) => b[1].count - a[1].count).slice(0, 5);

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
        <aside className="w-full md:w-64 bg-slate-900 text-white md:fixed h-auto md:h-full z-20">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center md:block">
                <div className="flex items-center gap-2 font-black text-xl text-teal-400">
                    <div className="w-8 h-8 bg-teal-500 rounded flex items-center justify-center text-white text-sm">A</div>
                    پنل مدیریت
                </div>
            </div>
            <nav className="p-4 space-y-2 flex md:block overflow-x-auto md:overflow-y-auto md:max-h-[calc(100vh-120px)]">
                <button onClick={() => setAdminTab('overview')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'overview' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <LayoutDashboard size={20}/> پیشخوان
                </button>
                <button onClick={() => setAdminTab('listings')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'listings' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <List size={20}/> آگهی‌ها
                    {adminListings.filter(l => l.status === 'pending' || l.status === 'payment_pending').length > 0 && 
                        <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full mr-auto md:ml-0 md:mr-auto">{adminListings.filter(l => l.status === 'pending' || l.status === 'payment_pending').length}</span>
                    }
                </button>
                <button onClick={() => setAdminTab('chats')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'chats' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <MessageCircle size={20}/> گفتگوها
                    {chatThreads.length > 0 && 
                        <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full mr-auto md:ml-0 md:mr-auto">{chatThreads.length}</span>
                    }
                </button>
                <button onClick={() => setAdminTab('users')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'users' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <Users size={20}/> کاربران
                </button>
                 <button onClick={() => setAdminTab('pages')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'pages' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <Layers size={20}/> مدیریت صفحات
                </button>
                <button onClick={() => setAdminTab('analytics')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'analytics' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <BarChart size={20}/> آنالیز رفتار کاربران
                </button>
                <button onClick={() => setAdminTab('discounts')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'discounts' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <Tag size={20}/> کدهای تخفیف
                </button>
                <button onClick={() => setAdminTab('settings')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'settings' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <Settings size={20}/> تنظیمات قیمت
                </button>
                <button onClick={() => navigate('/admin/listing-types')} className="shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-slate-400 hover:bg-slate-800 hover:text-white">
                    <Tag size={20}/> تنظیمات دسته‌های آگهی
                </button>
                <button onClick={() => setAdminTab('valuation_requests')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'valuation_requests' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <FileText size={20}/> درخواست‌های ارزشگذاری
                </button>
                <button onClick={() => setAdminTab('payments')} className={`shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${adminTab === 'payments' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/50' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                    <DollarSign size={20}/> پرداختی‌ها
                </button>
                <div className="hidden md:block border-t border-slate-800 my-4 pt-4">
                    <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-900/20 transition-all">
                        <X size={20}/> خروج
                    </button>
                </div>
            </nav>
        </aside>
        <main className="flex-1 md:mr-64 p-4 md:p-8 overflow-y-auto">
             {/* Admin Content */}
             {adminTab === 'overview' && adminStats && (
                <div className="space-y-6 animate-fadeIn">
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                             <p className="text-gray-500 text-sm font-medium mb-1">ارزش کل بازار</p>
                             <h3 className="text-2xl font-black text-gray-900">{formatCurrency(adminStats.totalMarketValue)}</h3>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                             <p className="text-gray-500 text-sm font-medium mb-1">کاربران فعال</p>
                             <h3 className="text-2xl font-black text-gray-900">{adminStats.totalUsers.toLocaleString()}</h3>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                             <p className="text-gray-500 text-sm font-medium mb-1">آگهی‌های فعال</p>
                             <h3 className="text-2xl font-black text-gray-900">{adminStats.totalListings}</h3>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                             <p className="text-gray-500 text-sm font-medium mb-1">مذاکرات جاری</p>
                             <h3 className="text-2xl font-black text-gray-900">{adminStats.activeOffers}</h3>
                        </div>
                    </div>
                </div>
            )}
             {adminTab === 'listings' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fadeIn">
                      <div className="p-6 border-b border-gray-100 flex flex-wrap gap-4 items-center">
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-xs font-bold text-gray-500 mb-1 block">فیلتر وضعیت</label>
                            <select 
                                value={adminFilter} 
                                onChange={(e) => setAdminFilter(e.target.value as any)}
                                className="w-full p-2 text-sm border rounded-lg outline-none"
                            >
                                <option value="all">همه وضعیت‌ها</option>
                                <option value="pending">در انتظار تایید</option>
                                <option value="approved">تایید شده</option>
                                <option value="rejected">رد شده</option>
                            </select>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-xs font-bold text-gray-500 mb-1 block">نوع واگذاری</label>
                            <select 
                                value={adminBrokerageFilter} 
                                onChange={(e) => setAdminBrokerageFilter(e.target.value as any)}
                                className="w-full p-2 text-sm border rounded-lg outline-none"
                            >
                                <option value="all">همه انواع</option>
                                <option value="direct">مستقیم</option>
                                <option value="brokerage">کارگزاری</option>
                            </select>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-xs font-bold text-gray-500 mb-1 block">دسته‌بندی</label>
                            <select 
                                value={adminCategoryFilter} 
                                onChange={(e) => setAdminCategoryFilter(e.target.value)}
                                className="w-full p-2 text-sm border rounded-lg outline-none"
                            >
                                <option value="all">همه دسته‌ها</option>
                                {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                      </div>
                      <div className="p-6">
                        <table className="w-full text-right">
                        <thead className="bg-gray-50 text-gray-500 text-sm border-b border-gray-200">
                            <tr>
                            <th className="p-4">آگهی</th>
                            <th className="p-4">دسته‌بندی</th>
                            <th className="p-4">نوع واگذاری</th>
                            <th className="p-4">درصد کارگزاری</th>
                            <th className="p-4">وضعیت</th>
                            <th className="p-4">مدارک تاییدی</th>
                            <th className="p-4">شماره فاکتور</th>
                            <th className="p-4">عملیات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredAdminListings.map(listing => (
                            <tr key={listing.id}>
                                <td className="p-4 font-bold text-gray-900">{listing.title}</td>
                                <td className="p-4 text-sm text-gray-600">{listing.category}</td>
                                <td className="p-4">
                                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${listing.brokerageType === 'brokerage' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                        {getBrokerageLabel(listing)}
                                    </span>
                                </td>
                                <td className="p-4">
                                    {listing.brokerageType === 'brokerage' && listing.brokeragePercentage ? (
                                        <span className="text-sm font-bold text-orange-700">{listing.brokeragePercentage}٪</span>
                                    ) : (
                                        <span className="text-xs text-gray-400">---</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    {(() => {
                                        console.log('Listing status for', listing.title, ':', listing.status, typeof listing.status);
                                        return getListingStatusBadge(listing.status);
                                    })()}
                                </td>
                                <td className="p-4">
                                    {hasAllApprovedVerificationDocs(listing) ? (
                                        <span className="text-xs px-2 py-1 rounded-full font-bold bg-emerald-100 text-emerald-700">تایید شده</span>
                                    ) : hasAllUploadedVerificationDocs(listing) ? (
                                        <span className="text-xs px-2 py-1 rounded-full font-bold bg-amber-100 text-amber-700">آماده بررسی</span>
                                    ) : hasListingVerificationDocs(listing) ? (
                                        <span className="text-xs px-2 py-1 rounded-full font-bold bg-orange-100 text-orange-700">ناقص</span>
                                    ) : (
                                        <span className="text-xs px-2 py-1 rounded-full font-bold bg-gray-100 text-gray-500">بدون مدرک</span>
                                    )}
                                </td>
                                <td className="p-4 font-mono text-xs text-gray-500 uppercase">{listing.invoiceNum || '---'}</td>
                                <td className="p-4">
                                    <div className="flex gap-2">
                                        {listing.status === 'approved' && listing.invoiceNum && (
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={() => {
                                                    // Find the listing fee transaction for this listing
                                                    const tx = adminPayments.find(p => p.listingId === listing.id && p.type === 'listing_fee');
                                                    if (tx) {
                                                        setSelectedInvoice({ ...tx, id: listing.invoiceNum });
                                                        setShowInvoiceModal(true);
                                                    } else if (listing.invoiceNum) {
                                                        setSelectedInvoice({
                                                            id: listing.invoiceNum,
                                                            userId: listing.ownerId,
                                                            listingTitle: listing.title,
                                                            amount: listing.price,
                                                            status: 'completed',
                                                            createdAt: listing.invoiceDate || listing.createdAt,
                                                            type: 'listing_fee',
                                                            user_name: getUserInfo(listing.ownerId).name
                                                        });
                                                        setShowInvoiceModal(true);
                                                    }
                                                }} 
                                                className="text-teal-600" 
                                                title="مشاهده فاکتور نهایی"
                                            >
                                                <FileText size={16}/>
                                            </Button>
                                        )}
                                        {hasListingVerificationDocs(listing) && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-blue-600"
                                                onClick={() => handleAdminViewDocs(listing)}
                                                title="مشاهده مدارک تاییدی"
                                            >
                                                <ExternalLink size={16}/>
                                            </Button>
                                        )}
                                        {isAwaitingAdminDecision(listing.status) && hasAllUploadedVerificationDocs(listing) && !hasAllApprovedVerificationDocs(listing) && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                                onClick={() => handleApproveVerificationDocs(listing)}
                                                title="تایید هر ۴ مدرک این آگهی"
                                            >
                                                تایید مدارک
                                            </Button>
                                        )}
                                        {isAwaitingAdminDecision(listing.status) && <Button size="sm" onClick={() => handleApproveClick(listing.id)} className="bg-teal-600">تایید</Button>}
                                        {isAwaitingAdminDecision(listing.status) && <Button size="sm" variant="danger" onClick={() => handleReject(listing.id)}>رد</Button>}
                                        <Button size="sm" variant="ghost" className="border border-gray-200" onClick={() => navigate(`/listings/edit/${listing.id}`)}>ویرایش</Button>
                                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50 border border-red-100" onClick={() => handleDeleteListing(listing.id)} title="حذف دائمی">
                                            <Trash2 size={16}/>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                            ))}
                            {filteredAdminListings.length === 0 && <tr><td colSpan={9} className="p-12 text-center text-gray-400">آگهی یافت نشد.</td></tr>}
                        </tbody>
                        </table>
                      </div>
                </div>
            )}
            
            {/* ADMIN CHATS TAB */}
            {adminTab === 'chats' && (
               <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fadeIn">
                   <div className="p-6 border-b border-gray-100">
                       <h2 className="font-bold text-lg mb-1">مدیریت گفتگوها</h2>
                       <p className="text-xs text-gray-500">همه گفتگوهای کاربران با صاحب آگهی یا ادمین در این بخش قابل مشاهده است.</p>
                   </div>

                   <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[560px]">
                        <div className="border-l border-gray-100 overflow-y-auto">
                            {chatThreads.length === 0 ? (
                                <div className="p-6 text-sm text-gray-400">گفتگویی ثبت نشده است.</div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {chatThreads.map(thread => (
                                        <button
                                            key={thread.id}
                                            onClick={() => setSelectedThreadId(thread.id)}
                                            className={`w-full text-right p-4 transition-colors ${selectedThreadId === thread.id ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
                                        >
                                            <div className="font-bold text-gray-900 text-sm">{thread.listingTitle}</div>
                                            <div className="text-xs text-gray-600 mt-1">طرف گفتگو: {thread.peerName}</div>
                                            <div className="text-xs text-gray-400 mt-1 truncate">{thread.lastMessage || '---'}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="lg:col-span-2 flex flex-col">
                            {!selectedThreadId ? (
                                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">یک گفتگو را انتخاب کنید.</div>
                            ) : (
                                <>
                                    <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[460px] bg-gray-50">
                                        {chatLoading ? (
                                            <div className="text-center text-sm text-gray-400 py-6">در حال بارگذاری...</div>
                                        ) : chatMessages.length === 0 ? (
                                            <div className="text-center text-sm text-gray-400 py-6">پیامی ثبت نشده است.</div>
                                        ) : (
                                            chatMessages.map(msg => {
                                                const own = msg.senderId === user?.id;
                                                return (
                                                    <div key={msg.id} className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
                                                        <div className={`max-w-[80%] px-4 py-2 rounded-xl text-sm ${own ? 'bg-teal-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                                                            <div className="text-[10px] mb-1 opacity-80">{msg.senderName || msg.senderId}</div>
                                                            <div>{msg.message}</div>
                                                            <div className={`text-[10px] mt-1 ${own ? 'text-teal-100' : 'text-gray-400'}`}>{new Date(msg.createdAt).toLocaleString('fa-IR')}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
               </div>
            )}

            {adminTab === 'users' && (
               <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fadeIn">
                   <div className="p-6 border-b border-gray-100">
                       <h2 className="font-bold text-lg">مدیریت کاربران سیستم</h2>
                       <p className="text-xs text-gray-500 mt-1">مشاهده لیست کامل کاربران و فعالیت‌های آن‌ها</p>
                   </div>
                   <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 text-gray-500 text-sm">
                                <tr>
                                    <th className="p-4">نام کاربر</th>
                                    <th className="p-4">شماره تماس</th>
                                    <th className="p-4">نقش</th>
                                    <th className="p-4">تعداد آگهی</th>
                                    <th className="p-4">ارزش کل آگهی‌ها</th>
                                    <th className="p-4">ایمیل</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {adminUsers.map(u => (
                                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                                                    <img
                                                      src={getUserAvatar(u.avatar)}
                                                      className="w-full h-full object-cover"
                                                      onError={(e) => {
                                                        const target = e.currentTarget as HTMLImageElement;
                                                        if (target.src !== DEFAULT_USER_AVATAR) {
                                                          target.src = DEFAULT_USER_AVATAR;
                                                        }
                                                      }}
                                                    />
                                                </div>
                                                <span className="font-bold text-gray-900">{u.name}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm font-mono text-gray-600" dir="ltr">{u.phone}</td>
                                        <td className="p-4 text-xs">
                                            <span className={`px-2 py-1 rounded-full font-bold ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {u.role === 'admin' ? 'مدیر' : 'کاربر عادی'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-lg font-bold">
                                                {getUserListingCount(u.id)}
                                            </span>
                                        </td>
                                        <td className="p-4 font-bold text-gray-900">{formatCurrency(getUserTotalValue(u.id))}</td>
                                        <td className="p-4 text-sm text-gray-500">{u.email || '---'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                   </div>
               </div>
            )}

             {adminTab === 'pages' && (
                isEditingPage ? (
                    <PageEditor initialData={editingPageData} onSave={handlePageSave} onCancel={() => { setIsEditingPage(false); setEditingPageData(undefined); }} />
                ) : (
                    <div className="space-y-8 animate-fadeIn">
                        {/* System Pages */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                                <Settings size={20} className="text-teal-600"/>
                                مدیریت صفحات اصلی سیستم
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {systemPages.map(p => (
                                    <div key={p.id} className="group border border-gray-200 p-5 rounded-2xl flex flex-col gap-4 bg-gray-50 hover:bg-white hover:shadow-md transition-all">
                                        <div className="flex justify-between items-start">
                                            <div className="w-10 h-10 bg-teal-100 text-teal-600 rounded-xl flex items-center justify-center">
                                                <FileText size={20}/>
                                            </div>
                                            <div className="bg-teal-50 text-teal-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-teal-100">سیستمی</div>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{p.title}</h3>
                                            <p className="text-xs text-gray-500">ویرایش محتوا، تصاویر و متون {p.title}</p>
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <Button 
                                                size="sm" 
                                                className="flex-1 text-xs gap-1.5"
                                                onClick={() => navigate(p.path)}
                                            >
                                                <ExternalLink size={14}/> مشاهده و ویرایش
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Custom Dynamic Pages */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    <PlusCircle size={20} className="text-blue-600"/>
                                    صفحات سفارشی (لندینگ پیج)
                                </h2>
                                <Button onClick={handleCreatePage} size="sm" className="gap-2">
                                    <Plus size={18}/> صفحه جدید
                                </Button>
                            </div>
                            
                            {adminPages.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Layers size={24} className="text-gray-400"/>
                                    </div>
                                    <p className="text-gray-500 font-medium">هنوز صفحه سفارشی ساخته نشده است.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {adminPages.map(p => (
                                        <div key={p.id} className="group border border-gray-200 p-5 rounded-2xl flex flex-col gap-4 bg-gray-50 hover:bg-white hover:shadow-md transition-all">
                                            <div className="flex justify-between items-start">
                                                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                                                    <Layers size={20}/>
                                                </div>
                                                <div className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-100">سفارشی</div>
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900 mb-1">{p.title}</h3>
                                                <p className="text-xs text-gray-500" dir="ltr">/{p.slug}</p>
                                            </div>
                                            <div className="flex gap-2 mt-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    className="flex-1 text-xs gap-1.5 border border-gray-200"
                                                    onClick={() => handleEditPage(p)}
                                                >
                                                    <Edit size={14}/> ویرایش
                                                </Button>
                                                <Button 
                                                    size="sm" 
                                                    variant="ghost" 
                                                    onClick={() => handleDeletePage(p.id)} 
                                                    className="text-red-500 hover:bg-red-50 border border-red-100"
                                                >
                                                    <Trash2 size={14}/>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )
             )}

             {adminTab === 'discounts' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-fadeIn">
                    <h2 className="font-bold text-lg mb-6 flex items-center gap-2"><Tag size={20}/> مدیریت کدهای تخفیف</h2>
                    
                    <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-4">ایجاد کد تخفیف جدید</h3>
                        <div className="flex flex-col md:flex-row items-end gap-4">
                            <div className="flex-1">
                                <Input 
                                    label="کد تخفیف"
                                    type="text"
                                    value={newDiscountCode.code || ''}
                                    onChange={(e) => setNewDiscountCode({...newDiscountCode, code: e.target.value})}
                                    dir="ltr"
                                    placeholder="مثال: NOROUZ1403"
                                />
                            </div>
                            <div className="flex-1">
                                <Input 
                                    label="درصد تخفیف"
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={newDiscountCode.discountPercent || ''}
                                    onChange={(e) => setNewDiscountCode({...newDiscountCode, discountPercent: parseInt(e.target.value) || 0})}
                                    dir="ltr"
                                />
                            </div>
                            <div className="flex-1">
                                <Input 
                                    label="سقف تخفیف (تومان - اختیاری)"
                                    type="text"
                                    value={newDiscountCode.maxDiscountAmount ? newDiscountCode.maxDiscountAmount.toLocaleString() : ''}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '') || '0', 10);
                                        setNewDiscountCode({...newDiscountCode, maxDiscountAmount: val});
                                    }}
                                    dir="ltr"
                                />
                            </div>
                            <div className="w-full md:w-60">
                                <label className="text-xs font-bold text-gray-500 mb-1 block">حوزه کارگزاری/مستقیم</label>
                                <select
                                    className="w-full p-2.5 text-sm border rounded-lg"
                                    value={newDiscountCode.brokerageScope || 'all'}
                                    onChange={(e) => setNewDiscountCode({ ...newDiscountCode, brokerageScope: e.target.value as any })}
                                >
                                    {BROKERAGE_SCOPE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-full md:w-72">
                                <label className="text-xs font-bold text-gray-500 mb-1 block">دسته‌های مجاز (اختیاری)</label>
                                <div className="flex gap-2 overflow-x-auto py-1">
                                    {LISTING_TYPE_OPTIONS.map(opt => {
                                        const selectedTypes = (newDiscountCode.applicableTypes || []) as ListingType[];
                                        const isSelected = selectedTypes.includes(opt.value);
                                        return (
                                            <button
                                                type="button"
                                                key={opt.value}
                                                onClick={() => setNewDiscountCode({
                                                    ...newDiscountCode,
                                                    applicableTypes: toggleListingTypeSelection(selectedTypes, opt.value)
                                                })}
                                                className={`shrink-0 px-3 py-1.5 rounded-full border text-xs font-bold transition-colors ${isSelected ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <Button onClick={handleCreateDiscount} className="w-full md:w-auto h-[42px]">ایجاد کد</Button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="p-4 text-sm font-bold text-gray-600">کد تخفیف</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">درصد</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">سقف تخفیف</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">اسکوپ کارگزاری</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">دسته‌های مجاز</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">وضعیت</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">عملیات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {adminDiscounts.map(discount => (
                                    <tr key={discount.id} className="hover:bg-gray-50">
                                        <td className="p-4 font-mono font-bold text-teal-600" dir="ltr">{discount.code}</td>
                                        <td className="p-4 font-bold text-gray-900">{discount.discountPercent}٪</td>
                                        <td className="p-4 text-sm text-gray-500">{discount.maxDiscountAmount ? `${discount.maxDiscountAmount.toLocaleString()} تومان` : 'بدون سقف'}</td>
                                        <td className="p-4 text-xs text-gray-600">{BROKERAGE_SCOPE_OPTIONS.find(o => o.value === (discount.brokerageScope || 'all'))?.label || 'همه'}</td>
                                        <td className="p-4 text-xs text-gray-600">{discount.applicableTypes && discount.applicableTypes.length > 0 ? discount.applicableTypes.map(tp => LISTING_TYPE_OPTIONS.find(o => o.value === tp)?.label || tp).join('، ') : 'همه دسته‌ها'}</td>
                                        <td className="p-4">
                                            <button 
                                                onClick={() => handleToggleDiscount(discount.id, !discount.isActive)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${discount.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                            >
                                                {discount.isActive ? 'فعال' : 'غیرفعال'}
                                            </button>
                                        </td>
                                        <td className="p-4">
                                            <Button size="sm" variant="danger" onClick={() => handleDeleteDiscount(discount.id)} className="text-xs">حذف</Button>
                                        </td>
                                    </tr>
                                ))}
                                {adminDiscounts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-gray-400">کد تخفیفی ثبت نشده است.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
             )}

             {adminTab === 'payments' && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="font-bold text-lg">مدیریت تراکنش‌ها و صورتحساب‌ها</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-right">
                                <thead className="bg-gray-50 text-gray-500 text-sm">
                                    <tr>
                                        <th className="p-4">کاربر</th>
                                        <th className="p-4">کد فاکتور</th>
                                        <th className="p-4">بابت</th>
                                        <th className="p-4">تخفیف</th>
                                        <th className="p-4">تاریخ</th>
                                        <th className="p-4">مبلغ</th>
                                        <th className="p-4">وضعیت</th>
                                        <th className="p-4">عملیات</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {adminPayments.map(tx => (
                                        <tr key={tx.id} className="hover:bg-gray-50 text-sm">
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900">{tx.user_name || 'ناشناس'}</div>
                                                <div className="text-[10px] text-gray-400 font-mono">{getUserInfo(tx.userId).phone}</div>
                                            </td>
                                            <td className="p-4 font-mono text-xs text-gray-500 uppercase">{tx.id.replace('tr_', '')}</td>
                                            <td className="p-4">
                                                <div className="font-medium text-gray-700">
                                                    {tx.type === 'listing_fee' ? 'ثبت آگهی' : tx.type === 'investment' ? 'سرمایه‌گذاری' : 'مشارکت'}
                                                </div>
                                                <div className="text-[10px] text-gray-400 truncate max-w-[150px]">{tx.listingTitle}</div>
                                            </td>
                                            <td className="p-4">
                                                {tx.metadata?.discount?.code ? (
                                                    <div className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full inline-block" dir="ltr">
                                                        {tx.metadata.discount.code}
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400">ندارد</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-xs text-gray-500" dir="ltr">{new Date(tx.createdAt).toLocaleDateString('fa-IR')}</td>
                                            <td className="p-4 font-black text-gray-900">{tx.amount.toLocaleString()}</td>
                                            <td className="p-4">
                                                <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${tx.status === 'completed' ? 'bg-green-100 text-green-700' : tx.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {tx.status === 'completed' ? 'پرداخت شده' : tx.status === 'failed' ? 'ناموفق' : 'در انتظار'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-1">
                                                <Button size="sm" variant="ghost" onClick={() => { setSelectedInvoice(tx); setShowInvoiceModal(true); }} className="text-teal-600 hover:bg-teal-50 gap-1 p-1 h-8">
                                                    <Eye size={14}/> مشاهده جزئیات
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => handleDeleteTransaction(tx.id)} className="text-red-600 hover:bg-red-50 gap-1 p-1 h-8">
                                                    <Trash2 size={14}/> حذف
                                                </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {adminPayments.length === 0 && (
                                        <tr><td colSpan={8} className="p-12 text-center text-gray-400">تراکنشی یافت نشد.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
             )}

             {adminTab === 'valuation_requests' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-fadeIn">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="font-bold text-lg flex items-center gap-2">
                            <FileText size={20}/> درخواست‌های ارزشگذاری
                        </h2>
                        <div className="flex gap-2">
                            <button onClick={() => setValuationFilter('all')} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${valuationFilter === 'all' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>همه</button>
                            <button onClick={() => setValuationFilter('pending')} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${valuationFilter === 'pending' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>در انتظار</button>
                            <button onClick={() => setValuationFilter('contacted')} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${valuationFilter === 'contacted' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>تماس گرفته شده</button>
                            <button onClick={() => setValuationFilter('completed')} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${valuationFilter === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>تکمیل شده</button>
                            <button onClick={() => setValuationFilter('rejected')} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${valuationFilter === 'rejected' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>رد شده</button>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="p-4 text-sm font-bold text-gray-600">نام</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">شماره تماس</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">نوع دارایی</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">توضیحات</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">تاریخ</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">وضعیت</th>
                                    <th className="p-4 text-sm font-bold text-gray-600">عملیات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {adminValuationRequests.filter(req => valuationFilter === 'all' || req.status === valuationFilter).map(req => (
                                    <tr key={req.id} className="hover:bg-gray-50">
                                        <td className="p-4 font-bold text-gray-900">{req.name}</td>
                                        <td className="p-4 font-mono text-sm text-gray-600" dir="ltr">{req.phone}</td>
                                        <td className="p-4 text-sm text-gray-600">{req.asset_type || '---'}</td>
                                        <td className="p-4 text-xs text-gray-500 max-w-xs truncate">{req.description || '---'}</td>
                                        <td className="p-4 text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString('fa-IR')}</td>
                                        <td className="p-4">
                                            {req.status === 'pending' && <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 w-fit"><Clock size={12}/> در انتظار</span>}
                                            {req.status === 'contacted' && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 w-fit"><Phone size={12}/> تماس گرفته شده</span>}
                                            {req.status === 'completed' && <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 w-fit"><CheckCircle size={12}/> تکمیل شده</span>}
                                            {req.status === 'rejected' && <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 w-fit"><XCircle size={12}/> رد شده</span>}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex gap-2">
                                                {req.status === 'pending' && (
                                                    <>
                                                        <Button size="sm" onClick={() => handleUpdateValuationStatus(req.id, 'contacted')} className="bg-blue-600 text-xs">تماس گرفته شد</Button>
                                                        <Button size="sm" variant="danger" onClick={() => handleUpdateValuationStatus(req.id, 'rejected')} className="text-xs">رد</Button>
                                                    </>
                                                )}
                                                {req.status === 'contacted' && (
                                                    <Button size="sm" onClick={() => handleUpdateValuationStatus(req.id, 'completed')} className="bg-green-600 text-xs">تکمیل شد</Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {adminValuationRequests.filter(req => valuationFilter === 'all' || req.status === valuationFilter).length === 0 && (
                                    <tr><td colSpan={7} className="p-12 text-center text-gray-400">درخواستی یافت نشد.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
             )}

             {adminTab === 'settings' && pricingConfig && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-fadeIn">
                    <h2 className="font-bold text-lg mb-6 flex items-center gap-2"><Settings size={20}/> تنظیمات قیمت‌گذاری</h2>
                    
                    {/* Base Fee */}
                    <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-4">تعرفه پایه</h3>
                        <div className="flex flex-col md:flex-row items-end gap-4 max-w-2xl">
                            <div className="w-full">
                                <Input 
                                    label="هزینه ثبت آگهی مستقیم (تومان)"
                                    type="text"
                                    value={pricingConfig.baseFee ? pricingConfig.baseFee.toLocaleString() : ''}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '') || '0', 10);
                                        setPricingConfig({...pricingConfig, baseFee: val});
                                    }}
                                    dir="ltr"
                                />
                            </div>
                            <div className="w-full">
                                <Input 
                                    label="هزینه ثبت آگهی کارگزاری (تومان)"
                                    type="text"
                                    value={pricingConfig.brokerageFee ? pricingConfig.brokerageFee.toLocaleString() : ''}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '') || '0', 10);
                                        setPricingConfig({...pricingConfig, brokerageFee: val});
                                    }}
                                    dir="ltr"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Services */}
                    <div className="mb-8">
                        <h3 className="font-bold text-gray-800 mb-4">خدمات قابل افزودن</h3>
                        <div className="space-y-4">
                            {pricingConfig.services.map((srv, idx) => (
                                <div key={srv.id} className="flex flex-col md:flex-row gap-4 items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md">
                                    {/* Reorder Buttons */}
                                    <div className="flex flex-row md:flex-col gap-1">
                                        <button 
                                            onClick={() => handleMoveService(idx, 'up')} 
                                            disabled={idx === 0}
                                            className="p-1.5 bg-gray-100 rounded-lg hover:bg-teal-50 hover:text-teal-600 text-gray-400 disabled:opacity-30 transition-colors"
                                            title="بالا"
                                        >
                                            <ArrowUp size={14}/>
                                        </button>
                                        <button 
                                            onClick={() => handleMoveService(idx, 'down')} 
                                            disabled={idx === pricingConfig.services.length - 1}
                                            className="p-1.5 bg-gray-100 rounded-lg hover:bg-teal-50 hover:text-teal-600 text-gray-400 disabled:opacity-30 transition-colors"
                                            title="پایین"
                                        >
                                            <ArrowDown size={14}/>
                                        </button>
                                    </div>

                                    <div className="w-full md:flex-1 grid grid-cols-1 md:grid-cols-6 gap-4">
                                         <div>
                                            <label className="text-xs font-bold text-gray-500 mb-1 block">عنوان خدمت</label>
                                            <input 
                                                className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                value={srv.title}
                                                onChange={(e) => handleUpdateService(srv.id, 'title', e.target.value)}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-bold text-gray-500 mb-1 block">توضیحات</label>
                                            <input 
                                                className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                value={srv.description}
                                                onChange={(e) => handleUpdateService(srv.id, 'description', e.target.value)}
                                            />
                                        </div>
                                         <div>
                                            <label className="text-xs font-bold text-gray-500 mb-1 block">قیمت (تومان)</label>
                                            <input 
                                                type="text"
                                                className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                value={srv.price ? srv.price.toLocaleString() : ''}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '') || '0', 10);
                                                    handleUpdateService(srv.id, 'price', val);
                                                }}
                                                dir="ltr"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 mb-1 block">اسکوپ خدمت</label>
                                            <select
                                                value={srv.brokerageScope || 'all'}
                                                onChange={(e) => handleUpdateService(srv.id, 'brokerageScope', e.target.value)}
                                                className="w-full p-2 text-sm border rounded-lg"
                                            >
                                                {BROKERAGE_SCOPE_OPTIONS.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 mb-1 block">دسته‌های مجاز</label>
                                            <div className="flex gap-1.5 overflow-x-auto py-1">
                                                {LISTING_TYPE_OPTIONS.map(opt => {
                                                    const selectedTypes = (srv.applicableTypes || []) as ListingType[];
                                                    const isSelected = selectedTypes.includes(opt.value);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={opt.value}
                                                            onClick={() => handleUpdateService(srv.id, 'applicableTypes', toggleListingTypeSelection(selectedTypes, opt.value))}
                                                            className={`shrink-0 px-2 py-1 rounded-full border text-[11px] font-bold transition-colors ${isSelected ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-end gap-2 w-full md:w-auto mt-2 md:mt-0">
                                         <div className="flex-1 md:w-32">
                                             <label className="text-xs font-bold text-gray-500 mb-1 block">آیکون</label>
                                             <select 
                                                value={srv.icon}
                                                onChange={(e) => handleUpdateService(srv.id, 'icon', e.target.value)}
                                                className="p-2 text-sm border rounded-lg w-full"
                                             >
                                                 {Object.keys(IconMap).map(k => <option key={k} value={k}>{k}</option>)}
                                             </select>
                                         </div>
                                         <Button variant="danger" size="sm" onClick={() => handleRemoveService(srv.id)} className="h-[38px] w-[38px] flex items-center justify-center p-0 rounded-lg" title="حذف">
                                            <Trash2 size={18}/>
                                         </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Add New Service */}
                        <div className="mt-6 bg-blue-50 p-4 rounded-xl border border-blue-100 border-dashed">
                             <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2"><Plus size={16}/> تعریف خدمت جدید</h4>
                             <div className="flex flex-col md:flex-row gap-3">
                                 <input placeholder="عنوان" className="p-2 text-sm border rounded-lg flex-1" value={newService.title} onChange={e => setNewService({...newService, title: e.target.value})}/>
                                 <input placeholder="توضیح کوتاه" className="p-2 text-sm border rounded-lg flex-[2]" value={newService.description} onChange={e => setNewService({...newService, description: e.target.value})}/>
                                 <input 
                                    type="text" 
                                    placeholder="قیمت" 
                                    className="p-2 text-sm border rounded-lg flex-1" 
                                    value={newService.price ? newService.price.toLocaleString() : ''} 
                                    onChange={e => {
                                        const val = parseInt(e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '') || '0', 10);
                                        setNewService({...newService, price: val});
                                    }}
                                    dir="ltr"
                                />
                                 <Button onClick={handleAddService} size="sm">افزودن</Button>
                             </div>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                 <div>
                                     <label className="text-xs font-bold text-gray-600 mb-1 block">اسکوپ خدمت</label>
                                     <select
                                        value={newService.brokerageScope || 'all'}
                                        onChange={e => setNewService({ ...newService, brokerageScope: e.target.value as any })}
                                        className="w-full p-2 text-sm border rounded-lg bg-white"
                                     >
                                        {BROKERAGE_SCOPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                     </select>
                                 </div>
                                 <div>
                                     <label className="text-xs font-bold text-gray-600 mb-1 block">دسته‌های مجاز</label>
                                     <div className="flex gap-2 overflow-x-auto py-1">
                                        {LISTING_TYPE_OPTIONS.map(opt => {
                                            const selectedTypes = (newService.applicableTypes || []) as ListingType[];
                                            const isSelected = selectedTypes.includes(opt.value);
                                            return (
                                                <button
                                                    type="button"
                                                    key={opt.value}
                                                    onClick={() => setNewService({ ...newService, applicableTypes: toggleListingTypeSelection(selectedTypes, opt.value) })}
                                                    className={`shrink-0 px-3 py-1.5 rounded-full border text-xs font-bold transition-colors ${isSelected ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                     </div>
                                 </div>
                             </div>
                        </div>
                    </div>

                    {/* Brokerage Terms */}
                    <div className="mb-8 p-6 bg-amber-50 rounded-xl border border-amber-200">
                        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <FileText size={18} className="text-amber-600"/>
                            شرایط و قوانین کارگزاری
                        </h3>
                        <p className="text-xs text-gray-600 mb-3">
                            این متن برای کاربرانی که گزینه کارگزاری را انتخاب می‌کنند نمایش داده می‌شود و باید آن را تایید کنند.
                        </p>
                        <textarea
                            className="w-full p-3 text-sm border rounded-lg focus:ring-2 focus:ring-amber-500 outline-none min-h-[200px] font-sans"
                            placeholder="شرایط و قوانین کارگزاری را اینجا وارد کنید..."
                            value={brokerageTermsText}
                            onChange={(e) => setBrokerageTermsText(e.target.value)}
                        />
                        <Button 
                            onClick={handleSaveBrokerageTerms} 
                            disabled={savingBrokerageTerms}
                            size="sm"
                            className="mt-3"
                        >
                            {savingBrokerageTerms ? 'در حال ذخیره...' : 'ذخیره شرایط کارگزاری'}
                        </Button>
                    </div>

                    <div className="pt-6 border-t border-gray-100 flex justify-between items-center">
                        <Button variant="danger" size="sm" onClick={() => { if(window.confirm('آیا از بازنشانی کل داده‌های سیستم اطمینان دارید؟')) api.resetData(); }}>
                            بازنشانی کل داده‌ها (توسعه)
                        </Button>
                        <Button onClick={handleSavePricing} disabled={savingPricing} className="w-40 shadow-xl">
                            {savingPricing ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
                        </Button>
                    </div>
                </div>
             )}

             {adminTab === 'analytics' && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-gray-900">آنالیز رفتار کاربران</h2>
                        <div className="text-sm text-gray-500">آخرین ۱۰۰۰ رویداد ثبت شده</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <div className="text-gray-500 text-xs mb-1">کل رویدادها</div>
                            <div className="text-2xl font-black text-teal-600">{adminAnalytics.length.toLocaleString()}</div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <div className="text-gray-500 text-xs mb-1">بازدید آگهی‌ها</div>
                            <div className="text-2xl font-black text-blue-600">{totalViews.toLocaleString()}</div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <div className="text-gray-500 text-xs mb-1">ثبت گفتگو</div>
                            <div className="text-2xl font-black text-purple-600">{totalOffers.toLocaleString()}</div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                            <div className="text-gray-500 text-xs mb-1">نرخ تبدیل (بازدید به گفتگو)</div>
                            <div className="text-2xl font-black text-orange-600">{conversionRate}%</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        {/* Top Searches */}
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Search size={18} className="text-gray-400"/> برترین جستجوها</h3>
                            <div className="space-y-3">
                                {topSearches.length > 0 ? topSearches.map(([query, count], idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm">
                                        <span className="text-gray-700 truncate">{query}</span>
                                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-bold">{count} بار</span>
                                    </div>
                                )) : <div className="text-xs text-gray-400 text-center py-4">داده‌ای موجود نیست</div>}
                            </div>
                        </div>

                        {/* Top Users */}
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Users size={18} className="text-gray-400"/> فعال‌ترین کاربران</h3>
                            <div className="space-y-3">
                                {topUsers.length > 0 ? topUsers.map(([id, data], idx) => (
                                    <div key={id} className="flex justify-between items-center text-sm cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors" onClick={() => setSelectedAnalyticsUser({id, name: data.name})}>
                                        <span className="text-teal-600 font-medium truncate hover:underline">{data.name}</span>
                                        <span className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full text-xs font-bold">{data.count} فعالیت</span>
                                    </div>
                                )) : <div className="text-xs text-gray-400 text-center py-4">داده‌ای موجود نیست</div>}
                            </div>
                        </div>

                        {/* Top Listings */}
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-gray-400"/> محبوب‌ترین آگهی‌ها</h3>
                            <div className="space-y-3">
                                {topListings.length > 0 ? topListings.map(([id, data], idx) => (
                                    <div key={id} className="flex justify-between items-center text-sm">
                                        <span className="text-gray-700 truncate max-w-[150px]">{data.title}</span>
                                        <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-xs font-bold">{data.count} بازدید/کلیک</span>
                                    </div>
                                )) : <div className="text-xs text-gray-400 text-center py-4">داده‌ای موجود نیست</div>}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="font-bold text-gray-900">گزارش لحظه‌ای فعالیت‌ها</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-right">
                                <thead className="bg-gray-50 text-gray-500 text-sm">
                                    <tr>
                                        <th className="p-4">زمان</th>
                                        <th className="p-4">کاربر</th>
                                        <th className="p-4">نوع فعالیت</th>
                                        <th className="p-4">هدف / جزئیات</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {adminAnalytics.map(event => (
                                        <tr key={event.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-4 text-xs font-mono text-gray-400" dir="ltr">
                                                {new Date(event.timestamp).toLocaleString('fa-IR')}
                                            </td>
                                            <td className="p-4">
                                                <div 
                                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
                                                    onClick={() => event.userId && setSelectedAnalyticsUser({id: event.userId, name: event.userName || 'کاربر ناشناس'})}
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 uppercase">
                                                        {event.userName ? event.userName.substring(0, 2) : 'AN'}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-teal-700 hover:underline">{event.userName || 'کاربر ناشناس'}</div>
                                                        <div className="text-[10px] text-gray-400 font-mono">{event.userId || '---'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                                                    event.type === 'login' ? 'bg-blue-100 text-blue-700' :
                                                    event.type === 'listing_click' ? 'bg-purple-100 text-purple-700' :
                                                    event.type === 'listing_view' ? 'bg-indigo-100 text-indigo-700' :
                                                    'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {event.type === 'login' ? 'ورود به سیستم' :
                                                     event.type === 'listing_click' ? 'کلیک روی آگهی' :
                                                     event.type === 'listing_view' ? 'مشاهده جزئیات' : event.type}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                {event.targetTitle ? (
                                                    <div className="text-sm">
                                                        <span className="text-gray-400 ml-1">آگهی:</span>
                                                        <span className="font-medium text-gray-700">{event.targetTitle}</span>
                                                    </div>
                                                ) : event.metadata?.method ? (
                                                    <div className="text-sm">
                                                        <span className="text-gray-400 ml-1">روش:</span>
                                                        <span className="font-medium text-gray-700">{event.metadata.method === 'phone' ? 'شماره همراه' : 'گوگل'}</span>
                                                    </div>
                                                ) : '---'}
                                            </td>
                                        </tr>
                                    ))}
                                    {adminAnalytics.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="p-12 text-center text-gray-400">هنوز هیچ فعالیتی ثبت نشده است.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
             )}

             {/* Invoice Modal */}
             {showInvoiceModal && selectedInvoice && (
                 <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                     <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scaleIn">
                         <div className="p-8">
                             {/* Invoice Header */}
                             <div className="flex justify-between items-start mb-10 border-b border-gray-100 pb-8">
                                 <div>
                                     <div className="flex items-center gap-2 font-black text-2xl text-teal-600 mb-2">
                                         TBIZ <span className="text-gray-400 font-light">| فاکتور رسمی</span>
                                     </div>
                                     <p className="text-sm text-gray-500">پلتفرم هوشمند مبادلات کسب‌وکار</p>
                                 </div>
                                 <div className="text-left">
                                     <div className="text-xs text-gray-400 mb-1 uppercase font-mono">Invoice Code</div>
                                     <div className="text-lg font-black text-gray-900 uppercase font-mono">{selectedInvoice.id.replace('tr_', '')}</div>
                                 </div>
                             </div>

                             {/* Billing Info */}
                             <div className="grid grid-cols-2 gap-8 mb-10 text-sm">
                                 <div>
                                     <h4 className="text-gray-400 mb-2 font-bold uppercase tracking-wider">اطلاعات مشتری</h4>
                                     <div className="font-bold text-gray-900">{selectedInvoice.user_name || user?.name}</div>
                                     <div className="text-gray-500 mt-1">{getUserInfo(selectedInvoice.userId).phone}</div>
                                 </div>
                                 <div className="text-left">
                                     <h4 className="text-gray-400 mb-2 font-bold uppercase tracking-wider">تاریخ صدور</h4>
                                     <div className="font-bold text-gray-900" dir="ltr">{new Date(selectedInvoice.createdAt).toLocaleDateString('fa-IR')}</div>
                                     <div className="mt-2">
                                         <span className={`px-2 py-1 rounded-lg text-[10px] font-black ${selectedInvoice.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                             {selectedInvoice.status === 'completed' ? 'تایید شده و پرداخت موفق' : 'در انتظار پرداخت'}
                                         </span>
                                     </div>
                                 </div>
                             </div>

                             {/* Invoice Items */}
                             <div className="mb-10">
                                 <table className="w-full text-right border-collapse">
                                     <thead>
                                         <tr className="text-gray-400 text-xs uppercase tracking-wider border-b border-gray-100">
                                             <th className="py-4 font-bold">شرح خدمات</th>
                                             <th className="py-4 font-bold text-left">مبلغ (تومان)</th>
                                         </tr>
                                     </thead>
                                     <tbody className="divide-y divide-gray-50">
                                         {selectedInvoice.metadata?.items ? (
                                             selectedInvoice.metadata.items.map((item: any, idx: number) => (
                                                 <tr key={idx}>
                                                     <td className="py-4 text-gray-700 font-medium">{item.title}</td>
                                                     <td className="py-4 text-left font-black text-gray-900">{item.price.toLocaleString()}</td>
                                                 </tr>
                                             ))
                                         ) : (
                                             <tr>
                                                 <td className="py-4 text-gray-700 font-medium">
                                                     {selectedInvoice.type === 'listing_fee' ? 'ثبت آگهی' : 'سرمایه‌گذاری'} - {selectedInvoice.listingTitle}
                                                 </td>
                                                 <td className="py-4 text-left font-black text-gray-900">{selectedInvoice.amount.toLocaleString()}</td>
                                             </tr>
                                         )}
                                         {selectedInvoice.metadata?.discount && (
                                             <tr className="text-rose-600 bg-rose-50/30">
                                                 <td className="py-4 px-2 font-bold text-xs">تخفیف (کد: {selectedInvoice.metadata.discount.code})</td>
                                                 <td className="py-4 px-2 text-left font-black">-{selectedInvoice.metadata.discount.amount.toLocaleString()}</td>
                                             </tr>
                                         )}
                                     </tbody>
                                     <tfoot>
                                         <tr className="border-t-2 border-gray-900">
                                             <td className="py-6 font-black text-gray-900 text-lg">مبلغ کل پرداختی</td>
                                             <td className="py-6 text-left font-black text-teal-600 text-2xl">{selectedInvoice.amount.toLocaleString()} <span className="text-xs">تومان</span></td>
                                         </tr>
                                     </tfoot>
                                 </table>
                             </div>

                             <div className="flex gap-4">
                                 <Button className="flex-1 bg-gray-900 hover:bg-black py-4 rounded-2xl flex items-center justify-center gap-2" onClick={() => window.print()}>
                                     <PlusCircle size={20}/> چاپ فاکتور
                                 </Button>
                                 {user?.role === 'admin' && (
                                     <Button variant="danger" className="flex-1 py-4 rounded-2xl flex items-center justify-center gap-2" onClick={() => handleDeleteTransaction(selectedInvoice.id)}>
                                         <Trash2 size={18}/> حذف فاکتور
                                     </Button>
                                 )}
                                 <Button variant="ghost" className="flex-1 border border-gray-200 py-4 rounded-2xl" onClick={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}>
                                     بستن
                                 </Button>
                             </div>
                         </div>
                     </div>
                 </div>
             )}

             {/* User Analytics Modal */}
             {selectedAnalyticsUser && (
                 <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                     <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                         <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                             <div>
                                 <h3 className="font-bold text-lg text-gray-900">تحلیل رفتار کاربر: {selectedAnalyticsUser.name}</h3>
                                 <p className="text-xs text-gray-500 font-mono mt-1">ID: {selectedAnalyticsUser.id}</p>
                             </div>
                             <button onClick={() => setSelectedAnalyticsUser(null)} className="p-2 bg-white rounded-full hover:bg-gray-200 text-gray-500 transition-colors shadow-sm">
                                 <X size={20} />
                             </button>
                         </div>
                         <div className="p-6 overflow-y-auto flex-1 bg-gray-50/50">
                             {/* User Journey Timeline */}
                             <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                                 {adminAnalytics
                                     .filter(e => e.userId === selectedAnalyticsUser.id)
                                     .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                     .map((event, idx) => (
                                         <div key={event.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                             {/* Icon */}
                                             <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-teal-100 text-teal-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                                                 {event.type === 'login' ? <LogOut size={16} className="rotate-180"/> :
                                                  event.type === 'listing_view' ? <Eye size={16}/> :
                                                  event.type === 'listing_click' ? <ArrowUpRight size={16}/> :
                                                  event.type === 'offer_submit' ? <MessageCircle size={16}/> :
                                                  event.type === 'search' ? <Search size={16}/> :
                                                  <Activity size={16}/>}
                                             </div>
                                             {/* Card */}
                                             <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                                 <div className="flex items-center justify-between mb-1">
                                                     <span className="font-bold text-gray-900 text-sm">
                                                         {event.type === 'login' ? 'ورود به سیستم' :
                                                          event.type === 'listing_view' ? 'مشاهده آگهی' :
                                                          event.type === 'listing_click' ? 'کلیک روی آگهی' :
                                                          event.type === 'offer_submit' ? 'ثبت گفتگو' :
                                                          event.type === 'search' ? 'جستجو' :
                                                          event.type === 'page_view' ? 'بازدید صفحه' :
                                                          event.type === 'listing_create' ? 'ثبت آگهی' : event.type}
                                                     </span>
                                                     <span className="text-[10px] text-gray-400 font-mono" dir="ltr">{new Date(event.timestamp).toLocaleString('fa-IR')}</span>
                                                 </div>
                                                 <div className="text-xs text-gray-600 mt-2">
                                                     {event.targetTitle && <div><span className="text-gray-400">هدف:</span> <span className="font-medium">{event.targetTitle}</span></div>}
                                                     {event.metadata?.query && <div><span className="text-gray-400">عبارت:</span> <span className="font-medium">"{event.metadata.query}"</span></div>}
                                                     {event.metadata?.method && <div><span className="text-gray-400">روش:</span> <span className="font-medium">{event.metadata.method}</span></div>}
                                                 </div>
                                             </div>
                                         </div>
                                     ))}
                             </div>
                         </div>
                     </div>
                 </div>
             )}
        </main>
      </div>
    );
  }

  // --- USER DASHBOARD RENDER (NEW LAYOUT) ---
  const UserSidebarItem = ({ id, label, icon: Icon, count }: any) => (
      <button 
          onClick={() => setActiveTab(id)}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all mb-1 ${activeTab === id ? 'bg-teal-600 text-white shadow-lg shadow-teal-200' : 'text-gray-600 hover:bg-gray-100'}`}
      >
          <div className="flex items-center gap-3">
              <Icon size={20} />
              <span className="font-medium">{label}</span>
          </div>
          {count > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'}`}>
                  {count}
              </span>
          )}
      </button>
  );

  const creatorCommunityListings = userListings.filter(l => l.type === 'micro_investment' || l.type === 'philanthropic');
  const contributorTransactions = userTransactions.filter(t => t.type === 'investment' || t.type === 'donation');
  const hasCreatorRole = creatorCommunityListings.length > 0;
  const hasContributorRole = contributorTransactions.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans">
        
        {/* --- USER SIDEBAR --- */}
        <aside className="w-full md:w-72 bg-white border-l border-gray-200 p-6 flex flex-col md:min-h-screen z-10 relative">
            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-gray-100">
                <div className="w-14 h-14 rounded-full bg-gray-100 border-2 border-teal-100 overflow-hidden shrink-0">
                    <img
                      src={getUserAvatar(userFormData.avatar || user?.avatar)}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        if (target.src !== DEFAULT_USER_AVATAR) {
                          target.src = DEFAULT_USER_AVATAR;
                        }
                      }}
                    />
                </div>
                <div className="overflow-hidden">
                    <h3 className="font-bold text-gray-900 truncate">{user?.name}</h3>
                    <p className="text-xs text-gray-500 font-mono truncate">{user?.phone}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-teal-50 text-teal-700 text-[10px] rounded border border-teal-100">پنل کاربری</span>
                    <div className="flex flex-wrap gap-1 mt-2">
                        {hasCreatorRole && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded border border-emerald-100">ایجادکننده پروژه</span>}
                        {hasContributorRole && <span className="px-2 py-0.5 bg-orange-50 text-orange-700 text-[10px] rounded border border-orange-100">مشارکت‌کننده</span>}
                        {!hasCreatorRole && !hasContributorRole && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded border border-gray-200">کاربر عادی</span>}
                    </div>
                </div>
            </div>

            <nav className="flex-1 space-y-1">
                <UserSidebarItem id="overview" label="پیشخوان" icon={LayoutDashboard} />
                <UserSidebarItem id="listings" label="آگهی‌های من" icon={List} count={userListings.length} />
                <UserSidebarItem id="chats" label="گفتگوها" icon={MessageCircle} count={chatThreads.length} />
                {hasCreatorRole && (
                    <UserSidebarItem id="creator_projects" label="پروژه‌های من (خرد/عام‌المنفعه)" icon={Layers} count={creatorCommunityListings.length} />
                )}
                {hasContributorRole && (
                    <UserSidebarItem id="community_participation" label="مشارکت‌های من" icon={Coins} count={contributorTransactions.length} />
                )}
                <UserSidebarItem id="payments" label="پرداخت‌ها و فاکتورها" icon={DollarSign} count={userPayments.length} />
                <div className="my-4 border-t border-gray-100"></div>
                <UserSidebarItem id="settings" label="تنظیمات حساب" icon={Settings} />
            </nav>

            <div className="mt-8 pt-4 border-t border-gray-100">
                <button onClick={() => { logout(); navigate('/'); }} className="w-full flex items-center gap-3 px-4 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors text-sm font-medium">
                    <LogOut size={18}/> خروج از حساب
                </button>
            </div>
        </aside>

        {/* --- USER CONTENT --- */}
        <main className="flex-1 p-4 md:p-8 overflow-y-auto">
             {/* Success Message Toast */}
             {showSuccessMessage && (
                <div className="mb-6 bg-green-100 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2 animate-fadeIn">
                    <CheckCircle size={20}/>
                    <span>آگهی شما با موفقیت ثبت شد و پس از بررسی منتشر خواهد شد.</span>
                </div>
             )}

             {activeTab === 'overview' && (
                <div className="animate-fadeIn space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">وضعیت کلی</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center"><Briefcase size={24}/></div>
                            <div>
                                <div className="text-3xl font-black text-gray-900">{userListings.length}</div>
                                <div className="text-sm text-gray-500">آگهی‌های من</div>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><ArrowUpRight size={24}/></div>
                            <div>
                                <div className="text-3xl font-black text-gray-900">{chatThreads.length}</div>
                                <div className="text-sm text-gray-500">گفتگوها</div>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><ArrowDownLeft size={24}/></div>
                            <div>
                                <div className="text-3xl font-black text-gray-900">{chatThreads.length}</div>
                                <div className="text-sm text-gray-500">گفتگوها</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                             <h3 className="font-bold text-gray-900">آخرین آگهی‌های شما</h3>
                             <Button size="sm" onClick={() => navigate('/create-listing')}>آگهی جدید</Button>
                        </div>
                        {userListings.length === 0 ? (
                            <p className="text-gray-400 text-center py-4">شما هنوز هیچ آگهی ثبت نکرده‌اید.</p>
                        ) : (
                            <div className="space-y-3">
                                {userListings.slice(0, 3).map(l => (
                                    <div key={l.id} className="flex justify-between items-center border-b border-gray-50 pb-2 last:border-0">
                                        <span className="truncate max-w-[200px] text-gray-700">{l.title}</span>
                                        {getListingStatusBadge(l.status)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
             )}

             {activeTab === 'listings' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fadeIn">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="font-bold text-gray-900 text-lg">آگهی‌های من</h2>
                        <Button size="sm" onClick={() => navigate('/create-listing')}>+ آگهی جدید</Button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 text-gray-500 text-sm">
                                <tr>
                                    <th className="p-4">عنوان</th>
                                    <th className="p-4">نوع</th>
                                    <th className="p-4">تاریخ</th>
                                    <th className="p-4">وضعیت</th>
                                    <th className="p-4">عملیات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {userListings.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="p-4 font-bold text-gray-900">
                                            <div>{item.title}</div>
                                            {item.invoiceNum && (
                                                <div className="text-[10px] text-teal-600 font-bold mt-1">شماره فاکتور: {item.invoiceNum}</div>
                                            )}
                                            {normalizeDashboardListingStatus(item.status) === 'pending' && (
                                                <div className="text-[11px] text-amber-700 font-bold mt-1">آگهی شما در انتظار تایید مدیر است.</div>
                                            )}
                                            {normalizeDashboardListingStatus(item.status) === 'payment_pending' && (
                                                <div className="text-[11px] text-blue-700 font-bold mt-1">آگهی شما در انتظار تکمیل/تایید پرداخت است.</div>
                                            )}
                                        </td>
                                        <td className="p-4 text-xs">
                                            <span className={`px-2 py-1 rounded-lg font-bold ${
                                                item.type === 'sale' ? 'bg-teal-50 text-teal-700' :
                                                item.type === 'investment' ? 'bg-purple-50 text-purple-700' :
                                                item.type === 'micro_investment' ? 'bg-emerald-50 text-emerald-700' :
                                                item.type === 'philanthropic' ? 'bg-rose-50 text-rose-700' : 'bg-gray-50 text-gray-700'
                                            }`}>
                                                {item.type === 'sale' ? 'فروش' : 
                                                 item.type === 'investment' ? 'سرمایه' : 
                                                 item.type === 'micro_investment' ? 'خرد' : 
                                                 item.type === 'philanthropic' ? 'خیریه' : item.type}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-500">{new Date(item.createdAt).toLocaleDateString('fa-IR')}</td>
                                        <td className="p-4">{getListingStatusBadge(item.status)}</td>
                                        <td className="p-4">
                                            <div className="flex gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => navigate(`/listings/${item.id}`)} title="مشاهده">
                                                    <Eye size={16}/>
                                                </Button>
                                                {(item.invoiceNum || userPayments.some(p => p.listingId === item.id && p.type === 'listing_fee')) && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => {
                                                            // Find the listing fee transaction for this listing
                                                            const tx = userPayments.find(p => p.listingId === item.id && p.type === 'listing_fee');
                                                            if (tx) {
                                                                // If we found a transaction, show its invoice modal but with the listing's invoiceNum
                                                                setSelectedInvoice({ ...tx, id: item.invoiceNum || tx.id });
                                                                setShowInvoiceModal(true);
                                                            } else if (item.invoiceNum) {
                                                                // Fallback: create a dummy transaction object for the modal
                                                                setSelectedInvoice({
                                                                    id: item.invoiceNum,
                                                                    userId: user?.id,
                                                                    listingTitle: item.title,
                                                                    amount: item.price,
                                                                    status: 'completed',
                                                                    createdAt: item.invoiceDate || item.createdAt,
                                                                    type: 'listing_fee',
                                                                    user_name: user?.name
                                                                });
                                                                setShowInvoiceModal(true);
                                                            }
                                                        }} 
                                                        className="text-teal-600" 
                                                        title="مشاهده فاکتور نهایی"
                                                    >
                                                        <FileText size={16}/>
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-600"
                                                    onClick={() => handleUserDeleteListing(item.id)}
                                                    title="حذف آگهی"
                                                >
                                                    <Trash2 size={16}/>
                                                </Button>
                                                {item.status === 'rejected' && (
                                                    <Button variant="ghost" size="sm" onClick={() => navigate(`/listings/edit/${item.id}`)} className="text-teal-600" title="ویرایش">
                                                        <Edit size={16}/>
                                                    </Button>
                                                )}
                                                {(item.type === 'micro_investment' || item.type === 'philanthropic') && (
                                                    <Button variant="ghost" size="sm" onClick={() => setReportForm({ ...reportForm, listingId: item.id })} className="text-blue-600" title="ثبت گزارش">
                                                        <FileText size={16}/>
                                                    </Button>
                                                )}
                                                {item.type === 'philanthropic' && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="text-orange-600"
                                                        title="تنظیم امتیاز"
                                                        onClick={() => {
                                                            const ratio = prompt('تعداد واحد به ازای هر ۱ امتیاز را وارد کنید (مثلاً ۱۰۰):', item.pointRatio?.toString() || '100');
                                                            if (ratio) {
                                                                api.updateListing(item.id, { pointRatio: parseInt(ratio) }).then(() => {
                                                                    loadData();
                                                                    alert('تنظیمات امتیاز با موفقیت بروزرسانی شد.');
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <Zap size={16}/>
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {userListings.length === 0 && (
                                    <tr><td colSpan={7} className="p-8 text-center text-gray-400">لیست خالی است.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Report Creation Form */}
                    {reportForm.listingId && (
                        <div className="p-6 border-t border-gray-100 bg-blue-50/30 animate-fadeIn">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                    <FileText size={18} className="text-blue-600"/>
                                    ثبت گزارش جدید برای آگهی: {userListings.find(l => l.id === reportForm.listingId)?.title}
                                </h3>
                                <button onClick={() => setReportForm({ ...reportForm, listingId: '' })} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input label="عنوان گزارش" value={reportForm.title} onChange={e => setReportForm({ ...reportForm, title: e.target.value })} />
                                <div>
                                    <label className="text-xs font-bold text-gray-500 mb-1 block">نوع گزارش</label>
                                    <select 
                                        value={reportForm.type}
                                        onChange={e => setReportForm({ ...reportForm, type: e.target.value as any })}
                                        className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                                    >
                                        <option value="performance">عملکردی</option>
                                        <option value="financial">مالی</option>
                                        <option value="social_impact">تاثیر اجتماعی</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="text-xs font-bold text-gray-500 mb-1 block">محتوای گزارش</label>
                                <textarea 
                                    className="w-full p-3 text-sm border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none min-h-[100px]"
                                    value={reportForm.content}
                                    onChange={e => setReportForm({ ...reportForm, content: e.target.value })}
                                    placeholder="شرح عملکرد و دستاوردهای اخیر..."
                                />
                            </div>
                            <div className="flex justify-end">
                                <Button onClick={handlePostReport} disabled={isPostingReport}>
                                    {isPostingReport ? 'در حال ثبت...' : 'ثبت و ابلاغ گزارش'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
             )}

             {activeTab === 'chats' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fadeIn">
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="font-bold text-gray-900 text-lg">گفتگوها</h2>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[520px]">
                        <div className="border-l border-gray-100 overflow-y-auto">
                            {chatThreads.length === 0 ? (
                                <div className="p-6 text-sm text-gray-400">گفتگویی ثبت نشده است.</div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {chatThreads.map(thread => (
                                        <button
                                            key={thread.id}
                                            onClick={() => setSelectedThreadId(thread.id)}
                                            className={`w-full text-right p-4 transition-colors ${selectedThreadId === thread.id ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
                                        >
                                            <div className="font-bold text-gray-900 text-sm">{thread.listingTitle}</div>
                                            <div className="text-xs text-gray-500 mt-1">{thread.peerName}</div>
                                            <div className="text-xs text-gray-400 mt-1 truncate">{thread.lastMessage || '---'}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="lg:col-span-2 flex flex-col">
                            {!selectedThreadId ? (
                                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">یک گفتگو را انتخاب کنید.</div>
                            ) : (
                                <>
                                    <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[420px] bg-gray-50">
                                        {chatLoading ? (
                                            <div className="text-center text-sm text-gray-400 py-6">در حال بارگذاری...</div>
                                        ) : chatMessages.length === 0 ? (
                                            <div className="text-center text-sm text-gray-400 py-6">پیامی ثبت نشده است.</div>
                                        ) : (
                                            chatMessages.map(msg => {
                                                const own = msg.senderId === user?.id;
                                                return (
                                                    <div key={msg.id} className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
                                                        <div className={`max-w-[80%] px-4 py-2 rounded-xl text-sm ${own ? 'bg-teal-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                                                            <div>{msg.message}</div>
                                                            <div className={`text-[10px] mt-1 ${own ? 'text-teal-100' : 'text-gray-400'}`}>{new Date(msg.createdAt).toLocaleString('fa-IR')}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                    <div className="p-4 border-t border-gray-100 bg-white">
                                        <div className="flex gap-2">
                                            <input
                                                value={chatInput}
                                                onChange={(e) => setChatInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendChatMessage();
                                                    }
                                                }}
                                                className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                                                placeholder="پیام خود را بنویسید..."
                                            />
                                            <Button onClick={handleSendChatMessage} disabled={chatSending || !chatInput.trim()}>
                                                {chatSending ? '...' : 'ارسال'}
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
             )}

             {activeTab === 'creator_projects' && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="font-bold text-lg mb-2 flex items-center gap-2"><Layers size={20} className="text-emerald-600"/> پروژه‌های ایجادشده توسط شما</h2>
                        <p className="text-sm text-gray-500">این بخش مخصوص پروژه‌های سرمایه‌گذاری خرد و عام‌المنفعه‌ای است که شما ایجاد کرده‌اید.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                                <div className="text-xs text-emerald-700 mb-1">تعداد پروژه‌های خرد</div>
                                <div className="text-2xl font-black text-emerald-900">{creatorCommunityListings.filter(l => l.type === 'micro_investment').length}</div>
                            </div>
                            <div className="p-4 bg-rose-50 rounded-xl border border-rose-100">
                                <div className="text-xs text-rose-700 mb-1">تعداد پروژه‌های عام‌المنفعه</div>
                                <div className="text-2xl font-black text-rose-900">{creatorCommunityListings.filter(l => l.type === 'philanthropic').length}</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100"><h2 className="font-bold text-lg">لیست پروژه‌ها</h2></div>
                        <div className="divide-y divide-gray-100">
                            {creatorCommunityListings.map(item => (
                                <div key={item.id} className="p-6 hover:bg-gray-50">
                                    <div className="flex justify-between items-start gap-3">
                                        <div>
                                            <h3 className="font-bold text-gray-900">{item.title}</h3>
                                            <div className="text-xs text-gray-500 mt-1">{new Date(item.createdAt).toLocaleDateString('fa-IR')}</div>
                                            <div className="mt-2">{getListingStatusBadge(item.status)}</div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => navigate(`/listings/${item.id}`)} title="مشاهده"><Eye size={16}/></Button>
                                            <Button variant="ghost" size="sm" className="text-teal-600" onClick={() => navigate(`/listings/edit/${item.id}`)} title="ویرایش"><Edit size={16}/></Button>
                                            <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => setReportForm({ ...reportForm, listingId: item.id })} title="ثبت گزارش"><FileText size={16}/></Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {creatorCommunityListings.length === 0 && (
                                <div className="p-10 text-center text-gray-400">پروژه‌ای در این بخش ثبت نشده است.</div>
                            )}
                        </div>
                    </div>
                </div>
             )}

             {(activeTab === 'community_participation' || activeTab === 'investments') && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                            <Award size={20} className="text-teal-600"/>
                            خلاصه مشارکت‌های شما
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                                <h3 className="text-sm font-bold text-emerald-800 mb-2">کل واحدهای سهامی</h3>
                                <div className="text-2xl font-black text-emerald-900">{contributorTransactions.filter(t => t.type === 'investment').reduce((acc, curr) => acc + curr.units, 0).toLocaleString()} واحد</div>
                            </div>
                            <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                                <h3 className="text-sm font-bold text-orange-800 mb-2">کل امتیاز عام‌المنفعه</h3>
                                <div className="text-2xl font-black text-orange-900">{contributorTransactions.filter(t => t.type === 'donation').reduce((acc, curr) => acc + Math.floor(curr.units / (curr.metadata?.pointRatio || 100)), 0).toLocaleString()} امتیاز</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100"><h2 className="font-bold text-lg">تراکنش‌های مشارکت</h2></div>
                        <div className="divide-y divide-gray-100">
                            {contributorTransactions.map(tx => (
                                <div key={tx.id} className="p-6 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h3 className="font-bold text-gray-900 cursor-pointer hover:text-teal-600" onClick={() => navigate(`/listings/${tx.listingId}`)}>{tx.listingTitle}</h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${tx.type === 'investment' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                    {tx.type === 'investment' ? 'سرمایه‌گذاری خرد' : 'مشارکت عام‌المنفعه'}
                                                </span>
                                                <span className="text-xs text-gray-400 font-mono" dir="ltr">{new Date(tx.createdAt).toLocaleDateString('fa-IR')}</span>
                                            </div>
                                        </div>
                                        <div className="text-left">
                                            <div className="text-lg font-black text-gray-900">{tx.amount.toLocaleString()} تومان</div>
                                            <div className="text-xs text-gray-500">{tx.units} واحد {tx.type === 'investment' ? 'سهام' : 'مشارکت'}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {contributorTransactions.length === 0 && (
                                <div className="p-12 text-center text-gray-400">
                                    <Coins size={48} className="mx-auto mb-4 opacity-20"/>
                                    <p>شما هنوز مشارکتی در پروژه‌ها ثبت نکرده‌اید.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex items-center gap-2">
                            <FileText size={20} className="text-teal-600"/>
                            <h2 className="font-bold text-lg">گزارش‌های ابلاغ‌شده</h2>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {userReports.map(report => (
                                <div key={report.id} className="p-6 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-gray-800">{report.title}</h4>
                                        <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-bold">{report.type === 'performance' ? 'عملکردی' : report.type === 'financial' ? 'مالی' : 'تاثیر اجتماعی'}</span>
                                    </div>
                                    <p className="text-sm text-gray-600 leading-relaxed mb-3">{report.content}</p>
                                    <div className="flex justify-between items-center text-xs text-gray-400">
                                        <span>مربوط به پروژه: {report.listingTitle}</span>
                                        <span dir="ltr">{new Date(report.date).toLocaleDateString('fa-IR')}</span>
                                    </div>
                                </div>
                            ))}
                            {userReports.length === 0 && (
                                <div className="p-12 text-center text-gray-400">
                                    <p>هنوز گزارشی برای مشارکت‌های شما ثبت نشده است.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
             )}

             {activeTab === 'payments' && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="font-bold text-lg">تاریخچه پرداخت‌ها و فاکتورها</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-right">
                                <thead className="bg-gray-50 text-gray-500 text-sm">
                                    <tr>
                                        <th className="p-4">کد فاکتور</th>
                                        <th className="p-4">بابت</th>
                                        <th className="p-4">تاریخ</th>
                                        <th className="p-4">مبلغ</th>
                                        <th className="p-4">وضعیت</th>
                                        <th className="p-4">عملیات</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {userPayments.map(tx => (
                                        <tr key={tx.id} className="hover:bg-gray-50">
                                            <td className="p-4 font-mono text-xs text-gray-500 uppercase">{tx.id.replace('tr_', '')}</td>
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900">
                                                    {tx.type === 'listing_fee' ? 'ثبت آگهی' : tx.type === 'investment' ? 'سرمایه‌گذاری' : 'مشارکت'}
                                                </div>
                                                <div className="text-xs text-gray-500">{tx.listingTitle}</div>
                                            </td>
                                            <td className="p-4 text-sm text-gray-600" dir="ltr">{new Date(tx.createdAt).toLocaleDateString('fa-IR')}</td>
                                            <td className="p-4 font-black text-gray-900">{tx.amount.toLocaleString()} تومان</td>
                                            <td className="p-4">
                                                <span className={`text-xs px-2 py-1 rounded-full font-bold ${tx.status === 'completed' ? 'bg-green-100 text-green-700' : tx.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {tx.status === 'completed' ? 'پرداخت شده' : tx.status === 'failed' ? 'ناموفق' : 'در انتظار'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <Button size="sm" variant="ghost" onClick={() => { setSelectedInvoice(tx); setShowInvoiceModal(true); }} className="text-blue-600 hover:bg-blue-50 gap-1">
                                                    <FileText size={16}/> مشاهده فاکتور
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {userPayments.length === 0 && (
                                        <tr><td colSpan={6} className="p-12 text-center text-gray-400">تراکنشی یافت نشد.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
             )}

             {activeTab === 'settings' && (
                <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-fadeIn">
                    <h2 className="font-bold text-lg mb-6 flex items-center gap-2"><Settings size={20}/> تنظیمات حساب</h2>
                    <div className="flex justify-center mb-6">
                        <div className="relative group w-24 h-24">
                            <img
                              src={getUserAvatar(userFormData.avatar || user?.avatar)}
                              className="w-full h-full rounded-full object-cover border-4 border-gray-100"
                              onError={(e) => {
                                const target = e.currentTarget as HTMLImageElement;
                                if (target.src !== DEFAULT_USER_AVATAR) {
                                  target.src = DEFAULT_USER_AVATAR;
                                }
                              }}
                            />
                            <label className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity text-white">
                                <Upload size={20}/>
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload}/>
                            </label>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <Input label="نام کامل" value={userFormData.name} onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}/>
                        <Input label="ایمیل" value={userFormData.email} onChange={(e) => setUserFormData({...userFormData, email: e.target.value})} dir="ltr"/>
                        <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 flex justify-between text-sm text-gray-600">
                            <span>شماره موبایل</span>
                            <span className="font-mono font-bold">{userFormData.phone}</span>
                        </div>
                        <Button className="w-full mt-4" onClick={handleSaveProfile} disabled={profileLoading}>{profileLoading ? '...' : 'ذخیره تغییرات'}</Button>
                    </div>
                </div>
             )}
        </main>
    </div>
  );
};

export default DashboardPage;
